<template>
    <div
        v-if="message.type === 'tool_status'"
        class="my-1 text-left text-xs text-gray-400"
    >
        {{ t("vue.ai_chat_tool_loaded") }}
        <span class="font-medium">{{ message.tool_name }}</span>
    </div>

    <!-- The answer to an explicitly typed command: rendered as data, never
         through the markdown renderer used for model prose. -->
    <div
        v-else-if="message.type === 'tool_result' && message.result"
        class="my-1"
    >
        <AiChatToolResultCard :result="message.result" />
    </div>

    <div
        v-else-if="hasAnythingToShow"
        class="flex items-start"
        :class="message.type === 'user' ? 'justify-end' : 'justify-start'"
    >
        <div ref="contentRef" class="max-w-[80%] @max-lg:max-w-full">
            <div
                v-if="originChip"
                class="mb-1 flex flex-wrap items-center gap-1"
                :class="
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                "
            >
                <component
                    :is="returnHref ? 'button' : 'span'"
                    :type="returnHref ? 'button' : undefined"
                    class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
                    :class="
                        returnHref
                            ? 'cursor-pointer transition hover:bg-[#FF421E]/10 hover:text-[#FF421E]'
                            : ''
                    "
                    :title="
                        returnHref
                            ? t('vue.ai_widget_return_to_page')
                            : originChip.title
                    "
                    @click="returnToSource"
                >
                    <component :is="originChip.icon" class="h-3 w-3 shrink-0" />
                    <span class="truncate">{{ originChip.label }}</span>
                    <ArrowUturnLeftIcon
                        v-if="returnHref"
                        class="h-3 w-3 shrink-0"
                    />
                </component>
            </div>

            <div
                v-if="hasBubble"
                class="overflow-hidden rounded-2xl px-4 py-2 text-sm"
                :class="
                    message.type === 'user'
                        ? 'whitespace-pre-wrap break-words bg-gray-800 text-white'
                        : 'border border-gray-200 bg-white text-gray-800'
                "
            >
                <AiChatMarkdown
                    v-if="message.type === 'assistant'"
                    :content="message.content ?? ''"
                />
                <template v-else-if="answers">
                    <div class="space-y-2">
                        <div
                            v-for="item in answers.items"
                            :key="item.question_id"
                        >
                            <p class="text-[11px] text-white/60">
                                {{ item.question }}
                            </p>
                            <p class="font-medium">{{ item.answer }}</p>
                        </div>
                        <p v-if="answers.free_text" class="whitespace-pre-wrap">
                            {{ answers.free_text }}
                        </p>
                    </div>
                </template>
                <template v-else>
                    <AiChatMessageImage
                        v-if="message.attachment"
                        :attachment="message.attachment"
                        :dark="true"
                    />
                    <template v-if="message.content">
                        <span
                            v-if="message.command"
                            class="font-mono text-xs"
                            >{{ message.content }}</span
                        >
                        <template v-else>{{ message.content }}</template>
                    </template>
                </template>
            </div>

            <AiChatQuestionCard
                v-if="questionSet && chatId !== null"
                :questions="questionSet"
                :chat-id="chatId"
                :message-id="message.id"
                :widget="widget"
                :disabled="readOnly || !latest"
                @submitted="emit('answered')"
            />

            <div v-if="navigation" class="mt-2 flex justify-start">
                <button
                    type="button"
                    class="inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full bg-[#FF421E] px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-[#E03A1A] active:scale-95"
                    :title="navigation.label"
                    @click="goToNavigationTarget"
                >
                    <ArrowRightCircleIcon class="h-4 w-4 shrink-0" />
                    <span class="truncate">{{
                        t("vue.ai_chat_navigate_to", {
                            page: navigation.label,
                        })
                    }}</span>
                </button>
            </div>

            <div
                v-if="message.type === 'user' && message.skill"
                class="mt-1 flex flex-wrap items-center gap-1 justify-end"
            >
                <span
                    class="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-[#FF421E]/10 px-2 py-0.5 text-[10px] font-medium text-[#FF421E]"
                    :title="t('vue.ai_skill_attached')"
                >
                    <SparklesIcon class="h-3 w-3 shrink-0" />
                    <span class="truncate">{{ message.skill.name }}</span>
                </span>
            </div>

            <div
                class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400"
                :class="
                    message.type === 'user' ? 'justify-end' : 'justify-start'
                "
            >
                <AiChatFeedbackButtons
                    v-if="message.type === 'assistant' && chatId !== null"
                    :chat-id="chatId"
                    :message-id="message.id"
                    :feedback="feedback"
                    :disabled="readOnly"
                    @change="feedback = $event"
                />
                <span class="font-medium">{{ roleLabel }}</span>
                <span v-if="timeLabel">· {{ timeLabel }}</span>
                <span v-if="durationLabel">· {{ durationLabel }}</span>
                <span v-if="tokensLabel">· {{ tokensLabel }}</span>
                <span v-if="modelLabel">· {{ modelLabel }}</span>
            </div>
        </div>

        <AiChatMessageActions
            v-if="message.type === 'assistant' && chatId !== null && hasBubble"
            class="mt-1 ml-1.5 shrink-0"
            :chat-id="chatId"
            :message-id="message.id"
            :default-title="defaultTitle"
            :compact="compactActions"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import {
    MapPinIcon,
    ChatBubbleLeftRightIcon,
    Squares2X2Icon,
    ArrowUturnLeftIcon,
    ArrowRightCircleIcon,
    SparklesIcon,
} from "@heroicons/vue/24/outline";
import type {
    IAiChatAnswerSet,
    IAiChatFeedback,
    IAiChatMessage,
    IAiChatMessageNavigation,
    IAiChatQuestionSet,
} from "@/types/AiChat/IAiChat";
import { useTranslations } from "@/composables/useTranslations";
import AiChatFeedbackButtons from "@/Pages/AiChat/components/AiChatFeedbackButtons.vue";
import AiChatToolResultCard from "@/Pages/AiChat/components/AiChatToolResultCard.vue";
import AiChatQuestionCard from "@/Pages/AiChat/components/AiChatQuestionCard.vue";

const props = withDefaults(
    defineProps<{
        message: IAiChatMessage;
        chatId?: number | null;
        readOnly?: boolean;
        widget?: boolean;
        latest?: boolean;
    }>(),
    { chatId: null, readOnly: false, widget: false, latest: false },
);

const emit = defineEmits<{ answered: [] }>();

// The thumb is held locally rather than written back into the parent's message
// object: the server already stored the verdict, this only keeps the button lit
// until the next poll re-delivers it.
const feedback = ref<IAiChatFeedback | null>(props.message.feedback ?? null);

watch(
    () => props.message.feedback,
    (value) => {
        feedback.value = value ?? null;
    },
);

const { t } = useTranslations();

// The three action icons collapse into a single popup menu when they would
// crowd the reply: either the message is too narrow, or it is shorter than the
// stacked toolset itself (which would then stick out below a short message).
const COMPACT_ACTIONS_WIDTH = 300;
const COMPACT_ACTIONS_HEIGHT = 80;
const contentRef = ref<HTMLElement | null>(null);
const compactActions = ref(false);
let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
    if (contentRef.value === null || typeof ResizeObserver === "undefined") {
        return;
    }
    resizeObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect;
        compactActions.value =
            width < COMPACT_ACTIONS_WIDTH || height < COMPACT_ACTIONS_HEIGHT;
    });
    resizeObserver.observe(contentRef.value);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});

const defaultTitle = computed(() => {
    const raw = props.message.content ?? "";
    const withoutBlocks = raw.replace(
        /```(dmarceye-chart|dmarceye-stats)[\s\S]*?```/g,
        "",
    );
    for (const line of withoutBlocks.split("\n")) {
        const cleaned = line
            .replace(/^[#*\-\s>]+/, "")
            .replace(/[*`]/g, "")
            .trim();
        if (cleaned !== "") {
            return cleaned.slice(0, 120);
        }
    }
    return t("vue.ai_chat_role_assistant");
});

const roleLabel = computed(() =>
    props.message.type === "user"
        ? t("vue.ai_chat_role_you")
        : t("vue.ai_chat_role_assistant"),
);

const timeLabel = computed(() => {
    if (!props.message.created_at) {
        return "";
    }
    const date = new Date(props.message.created_at);
    if (isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
});

const durationLabel = computed(() => {
    const ms = props.message.meta?.duration_ms;
    if (typeof ms !== "number") {
        return "";
    }
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
});

const tokensLabel = computed(() => {
    const tokens = props.message.meta?.tokens;
    if (typeof tokens !== "number") {
        return "";
    }
    return `${new Intl.NumberFormat().format(tokens)} ${t("vue.ai_chat_tokens")}`;
});

const modelLabel = computed(() => props.message.meta?.model ?? "");

// Every jump out of a message carries two things the widget reads on arrival:
// the conversation (ai_widget_chat) and the message the user left from
// (ai_widget_message), so the mini chat re-opens scrolled to that exact bubble
// instead of at the bottom of a transcript the user has to re-find their place
// in. Optimistic messages carry a negative placeholder id and are never linked.
const withChatParams = (path: string): string => {
    const target = new URL(path, window.location.origin);
    if (props.chatId !== null) {
        target.searchParams.set("ai_widget_chat", String(props.chatId));
        if (props.message.id > 0) {
            target.searchParams.set(
                "ai_widget_message",
                String(props.message.id),
            );
        }
    }
    return target.pathname + target.search + target.hash;
};

// A widget-originated message can send the user back to the exact page they
// asked from, re-opening the mini chat on that page with this conversation
// active.
const returnHref = computed<string | null>(() => {
    const origin = props.message.origin;
    if (!origin || origin.source !== "widget" || !origin.url) {
        return null;
    }
    return withChatParams(origin.url);
});

// The page the assistant offered to take the user to. The destination is built
// server-side from an allowlisted route name, but the shape is re-checked here
// too: a protocol-relative "//host" path also starts with a slash and would
// leave the application, so navigation only ever runs on a single-slash path.
const navigation = computed<IAiChatMessageNavigation | null>(() => {
    const target = props.message.navigation;
    if (!target || props.message.type !== "assistant") {
        return null;
    }
    if (!target.path.startsWith("/") || target.path.startsWith("//")) {
        return null;
    }
    return target;
});

const questionSet = computed<IAiChatQuestionSet | null>(() => {
    const set = props.message.questions;
    if (!set || props.message.type !== "assistant" || set.answered) {
        return null;
    }
    return set.questions.length > 0 ? set : null;
});

const answers = computed<IAiChatAnswerSet | null>(() => {
    const set = props.message.answers;
    if (!set || props.message.type !== "user") {
        return null;
    }
    return set.items.length > 0 || set.free_text !== null ? set : null;
});

// A turn offering only a question form or a page jump has no text of its own.
const hasBubble = computed(() => {
    if (props.message.type === "assistant") {
        return (props.message.content ?? "").trim() !== "";
    }
    return (
        answers.value !== null ||
        (props.message.attachment ?? null) !== null ||
        (props.message.content ?? "").trim() !== ""
    );
});

// Answering a textless question turn removes its card too, leaving nothing to render.
const hasAnythingToShow = computed(
    () =>
        hasBubble.value ||
        questionSet.value !== null ||
        navigation.value !== null,
);

// Carries the conversation along, so the mini chat re-opens on the target page
// with this exact chat active — and on the reply that offered the jump — instead
// of dropping the user into a blank widget.
const goToNavigationTarget = (): void => {
    if (navigation.value === null) {
        return;
    }
    router.visit(withChatParams(navigation.value.path));
};

const returnToSource = (): void => {
    if (returnHref.value !== null) {
        router.visit(returnHref.value);
    }
};

// Shows where a user message was asked from: the mini chat (optionally with the
// page it was scoped to) or the full-page chat. A widget message always states
// "Mini chat" so its origin is explicit, plus the page/URL when present.
const originChip = computed(() => {
    const origin = props.message.origin;
    if (!origin || props.message.type !== "user") {
        return null;
    }

    if (origin.source === "widget") {
        const urlPath = origin.url ? origin.url.split("?")[0] : null;
        const page = origin.page ?? urlPath;
        const mini = t("vue.ai_widget_origin_mini");
        return {
            icon: page ? MapPinIcon : ChatBubbleLeftRightIcon,
            label: page ? `${mini} · ${page}` : mini,
            title: page
                ? `${t("vue.ai_widget_origin_page")}${urlPath ? ` · ${urlPath}` : ""}`
                : mini,
        };
    }

    return {
        icon: Squares2X2Icon,
        label: t("vue.ai_widget_origin_full"),
        title: t("vue.ai_widget_origin_full"),
    };
});
</script>
